import React from "react";
import {
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Bold, Italic, Underline, Strikethrough, Trash2, Copy, BringToFront, SendToBack,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  AlignHorizontalJustifyCenter, AlignVerticalJustifyCenter, ChevronUp, ChevronDown, Plus,
} from "lucide-react";
import { ZP } from "../compile";
import { PAPER_PRESETS, FONT_FAMILIES, FORMAT_OPTIONS, paperFromPreset, newColumn, cloneWithNewIds, uid } from "../schema";
import { DOC_TYPES, getDocType } from "../doc-types";
import FormulaInput from "./formula-input";
import { Section, Field, Grid, NumInput, TextInput, Select, ColorInput, Check, Segmented, IconBtn } from "./ui";

const ALIGN = [
  { id: "left", icon: <AlignLeft size={14} />, title: "Left" },
  { id: "center", icon: <AlignCenter size={14} />, title: "Center" },
  { id: "right", icon: <AlignRight size={14} />, title: "Right" },
  { id: "justify", icon: <AlignJustify size={14} />, title: "Justify" },
];
const VALIGN = [
  { id: "top", label: "Top" },
  { id: "middle", label: "Middle" },
  { id: "bottom", label: "Bottom" },
];
const SIDES = [
  { id: "t", label: "Top" },
  { id: "r", label: "Right" },
  { id: "b", label: "Bottom" },
  { id: "l", label: "Left" },
];

function FormatSelect({ value, onChange }) {
  const known = FORMAT_OPTIONS.some((o) => o.id === (value || ""));
  return (
    <div className="flex flex-col gap-1">
      <Select value={known ? value || "" : "__custom"} onChange={(v) => onChange(v === "__custom" ? value || "fixed:2" : v)} options={[...FORMAT_OPTIONS.map((o) => ({ value: o.id, label: o.label })), { value: "__custom", label: "Custom…" }]} />
      {!known && <TextInput mono value={value} onChange={onChange} placeholder="e.g. date:DD-MMM-YY | upper" />}
    </div>
  );
}

// ------------------------------------------------------------------ style
function StyleEditor({ el, set }) {
  const st = el.style || {};
  const s = (patch, key) => set((x) => (x.style = { ...(x.style || {}), ...patch }), key);
  const sides = !st.borders || st.borders === "all" ? ["t", "r", "b", "l"] : String(st.borders).split("").filter((c) => "trbl".includes(c));
  const isTextual = el.type === "text" || el.type === "field" || el.type === "barcode";
  return (
    <>
      {el.type === "line" ? (
        <Section title="Line">
          <Grid>
            <Field label="Thickness (pt)">
              <NumInput value={st.lineWidth ?? 0.75} step={0.25} min={0.1} onChange={(v) => s({ lineWidth: v }, "lw")} />
            </Field>
            <Field label="Style">
              <Select value={st.lineStyle || "solid"} onChange={(v) => s({ lineStyle: v })} options={["solid", "dashed", "dotted", "double"]} />
            </Field>
          </Grid>
          <Field label="Colour">
            <ColorInput value={st.color || "#000000"} onChange={(v) => s({ color: v }, "lc")} allowEmpty={false} />
          </Field>
        </Section>
      ) : (
        <>
          {isTextual && (
            <Section title="Text">
              <Grid>
                <Field label="Font">
                  <Select value={st.fontFamily || ""} onChange={(v) => s({ fontFamily: v || undefined })} options={[{ value: "", label: "Default" }, ...FONT_FAMILIES]} />
                </Field>
                <Field label="Size (pt)">
                  <NumInput value={st.fontSize ?? 9} step={0.5} min={3} max={72} onChange={(v) => s({ fontSize: v }, "fs")} />
                </Field>
              </Grid>
              <div className="flex flex-wrap gap-2 items-center">
                <Segmented
                  multi
                  value={[st.bold && "b", st.italic && "i", st.underline && "u", st.strike && "s"].filter(Boolean)}
                  onChange={(v) => s({ bold: v.includes("b"), italic: v.includes("i"), underline: v.includes("u"), strike: v.includes("s") })}
                  options={[
                    { id: "b", icon: <Bold size={14} />, title: "Bold" },
                    { id: "i", icon: <Italic size={14} />, title: "Italic" },
                    { id: "u", icon: <Underline size={14} />, title: "Underline" },
                    { id: "s", icon: <Strikethrough size={14} />, title: "Strike" },
                  ]}
                />
                <Segmented value={st.align || "left"} onChange={(v) => s({ align: v })} options={ALIGN} />
              </div>
              <Grid>
                <Field label="Vertical">
                  <Select value={st.valign || "top"} onChange={(v) => s({ valign: v })} options={VALIGN.map((o) => ({ value: o.id, label: o.label }))} />
                </Field>
                <Field label="Line height">
                  <NumInput value={st.lineHeight ?? 1.2} step={0.05} min={0.6} max={4} onChange={(v) => s({ lineHeight: v }, "lh")} />
                </Field>
              </Grid>
              <Field label="Text colour">
                <ColorInput value={st.color} onChange={(v) => s({ color: v }, "tc")} />
              </Field>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Check checked={st.wrap !== false} onChange={(v) => s({ wrap: v })} label="Wrap text" />
                <Check checked={!!st.clip} onChange={(v) => s({ clip: v })} label="Clip overflow" />
                <Check checked={!!st.uppercase} onChange={(v) => s({ uppercase: v })} label="UPPERCASE" />
              </div>
            </Section>
          )}
          {el.type === "qr" && (
            <Section title="Colour">
              <ColorInput value={st.color || "#000000"} onChange={(v) => s({ color: v }, "qc")} allowEmpty={false} />
            </Section>
          )}
          <Section title="Box" defaultOpen={el.type === "rect"}>
            <Field label="Fill">
              <ColorInput value={st.bg} onChange={(v) => s({ bg: v }, "bg")} />
            </Field>
            <Grid>
              <Field label="Border (pt)">
                <NumInput value={st.borderWidth ?? 0} step={0.25} min={0} onChange={(v) => s({ borderWidth: v }, "bw")} />
              </Field>
              <Field label="Border style">
                <Select value={st.borderStyle || "solid"} onChange={(v) => s({ borderStyle: v })} options={["solid", "dashed", "dotted", "double"]} />
              </Field>
            </Grid>
            {(st.borderWidth || 0) > 0 && (
              <>
                <Field label="Border colour">
                  <ColorInput value={st.borderColor || "#000000"} onChange={(v) => s({ borderColor: v }, "bc")} allowEmpty={false} />
                </Field>
                <Field label="Sides">
                  <Segmented multi value={sides} onChange={(v) => s({ borders: v.length === 4 ? "all" : v.join("") || "none" })} options={SIDES} />
                </Field>
              </>
            )}
            <Grid>
              <Field label="Padding (mm)">
                <NumInput value={st.padding ?? 0} step={0.25} min={0} onChange={(v) => s({ padding: v }, "pd")} />
              </Field>
              <Field label="Corner radius (mm)">
                <NumInput value={st.radius ?? 0} step={0.5} min={0} onChange={(v) => s({ radius: v }, "rd")} />
              </Field>
            </Grid>
            <Field label="Opacity">
              <NumInput value={st.opacity ?? 1} step={0.05} min={0.05} max={1} onChange={(v) => s({ opacity: v }, "op")} />
            </Field>
          </Section>
        </>
      )}
    </>
  );
}

// ------------------------------------------------------------------ element
function ElementProps({ el, band, template, sample, setEl, update, setSelection }) {
  const set = (fn, key) => setEl(fn, key);
  const scope = { kind: "doc" };
  const onImage = (file) => {
    if (!file) return;
    if (file.size > 600 * 1024) {
      window.alert("Please use an image smaller than 600 KB; it is stored inside the format.");
      return;
    }
    const r = new FileReader();
    r.onload = () => set((x) => (x.src = r.result));
    r.readAsDataURL(file);
  };
  const arrange = (dir) =>
    update((t) => {
      const b = t.bands.find((q) => q.id === band.id);
      const i = b.elements.findIndex((q) => q.id === el.id);
      const [x] = b.elements.splice(i, 1);
      if (dir === "front") b.elements.push(x);
      else b.elements.unshift(x);
    });
  const TYPE_LABEL = { text: "Text", field: "Field / Formula", image: "Image", line: "Line", rect: "Box", qr: "QR code", barcode: "Barcode", table: "Table" };
  return (
    <>
      <div className="px-3 py-2 flex items-center justify-between border-b border-EAECF0">
        <span className="text-xs font-semibold text-000000">{TYPE_LABEL[el.type] || el.type}</span>
        <div className="flex">
          <IconBtn title="Bring to front" onClick={() => arrange("front")}>
            <BringToFront size={14} />
          </IconBtn>
          <IconBtn title="Send to back" onClick={() => arrange("back")}>
            <SendToBack size={14} />
          </IconBtn>
          <IconBtn
            title="Duplicate (Ctrl+D)"
            onClick={() => {
              const c = cloneWithNewIds(el);
              c.id = uid(el.type.slice(0, 2));
              c.x += 2;
              c.y += 2;
              update((t) => t.bands.find((q) => q.id === band.id).elements.push(c));
              setSelection({ bandId: band.id, ids: [c.id], colId: null });
            }}
          >
            <Copy size={14} />
          </IconBtn>
          <IconBtn
            danger
            title="Delete (Del)"
            onClick={() => {
              update((t) => {
                const b = t.bands.find((q) => q.id === band.id);
                b.elements = b.elements.filter((q) => q.id !== el.id);
              });
              setSelection({ bandId: band.id, ids: [], colId: null });
            }}
          >
            <Trash2 size={14} />
          </IconBtn>
        </div>
      </div>

      {el.type === "text" && (
        <Section title="Content">
          <FormulaInput
            mode="template"
            rows={4}
            value={el.content}
            onChange={(v) => set((x) => (x.content = v), "content")}
            template={template}
            sample={sample}
            scope={scope}
            placeholder="Text with {{ field }} or {{ formula | money }}"
          />
          <p className="text-[10px] text-667085 leading-snug">
            Put fields and formulas inside <span className="font-mono">{"{{ }}"}</span>, add a format after <span className="font-mono">|</span> (e.g.{" "}
            <span className="font-mono">{"{{ created_date | date:'DD MMM YYYY' }}"}</span>). Wrap words in <span className="font-mono">**bold**</span>.
          </p>
        </Section>
      )}
      {(el.type === "field" || el.type === "qr" || el.type === "barcode") && (
        <Section title={el.type === "field" ? "Formula" : "Value to encode"}>
          <FormulaInput value={el.expr} onChange={(v) => set((x) => (x.expr = v), "expr")} template={template} sample={sample} scope={scope} format={el.type === "field" ? el.format : ""} placeholder="e.g. order.sot_total_amount - order.sot_total_discount" />
          {el.type === "field" && (
            <>
              <Field label="Format">
                <FormatSelect value={el.format} onChange={(v) => set((x) => (x.format = v))} />
              </Field>
              <Grid>
                <Field label="Prefix">
                  <TextInput value={el.prefix} onChange={(v) => set((x) => (x.prefix = v), "prefix")} placeholder="₹ " />
                </Field>
                <Field label="Suffix">
                  <TextInput value={el.suffix} onChange={(v) => set((x) => (x.suffix = v), "suffix")} placeholder=" /-" />
                </Field>
              </Grid>
            </>
          )}
          {el.type === "qr" && (
            <Field label="Error correction">
              <Select value={el.ecl || "M"} onChange={(v) => set((x) => (x.ecl = v))} options={[{ value: "L", label: "Low (7%)" }, { value: "M", label: "Medium (15%)" }, { value: "Q", label: "Quartile (25%)" }, { value: "H", label: "High (30%)" }]} />
            </Field>
          )}
          {el.type === "barcode" && <Check checked={el.showText !== false} onChange={(v) => set((x) => (x.showText = v))} label="Show text under the bars" />}
        </Section>
      )}
      {el.type === "image" && (
        <Section title="Image">
          <Field label="From a field (e.g. store.company_logo)">
            <FormulaInput value={el.srcExpr} onChange={(v) => set((x) => (x.srcExpr = v), "srcExpr")} template={template} sample={sample} scope={scope} rows={1} showResult={false} />
          </Field>
          <Field label="Or a fixed image">
            <TextInput value={el.src && el.src.startsWith("data:") ? "(uploaded image)" : el.src} onChange={(v) => set((x) => (x.src = v), "src")} placeholder="https://…" />
          </Field>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-primary cursor-pointer border border-primary rounded px-2 py-1">
              Upload…
              <input type="file" accept="image/*" className="hidden" onChange={(e) => onImage(e.target.files[0])} />
            </label>
            {el.src && (
              <button type="button" className="text-[11px] text-F51D00" onClick={() => set((x) => (x.src = ""))}>
                Remove
              </button>
            )}
          </div>
          <Field label="Fit">
            <Select value={el.fit || "contain"} onChange={(v) => set((x) => (x.fit = v))} options={[{ value: "contain", label: "Fit inside" }, { value: "cover", label: "Fill (crop)" }, { value: "fill", label: "Stretch" }]} />
          </Field>
        </Section>
      )}
      {el.type === "line" && (
        <Section title="Direction">
          <Segmented
            value={el.orientation || "h"}
            onChange={(v) =>
              set((x) => {
                if (v === x.orientation) return;
                x.orientation = v;
                const w = x.w;
                x.w = x.h;
                x.h = w;
              })
            }
            options={[{ id: "h", label: "Horizontal" }, { id: "v", label: "Vertical" }]}
          />
        </Section>
      )}

      <Section title="Position & size (mm)">
        <Grid cols={4}>
          <Field label="X">
            <NumInput value={el.x} onChange={(v) => set((x) => (x.x = v), "x")} />
          </Field>
          <Field label="Y">
            <NumInput value={el.y} onChange={(v) => set((x) => (x.y = v), "y")} />
          </Field>
          <Field label="W">
            <NumInput value={el.w} min={0.5} onChange={(v) => set((x) => (x.w = v), "w")} />
          </Field>
          <Field label="H">
            <NumInput value={el.h} min={0.5} onChange={(v) => set((x) => (x.h = v), "h")} />
          </Field>
        </Grid>
        {el.type !== "line" && (
          <Field label="Rotation (°)">
            <NumInput value={el.rotate || 0} step={5} min={-360} max={360} onChange={(v) => set((x) => (x.rotate = v), "rot")} />
          </Field>
        )}
      </Section>

      <StyleEditor el={el} set={set} />

      <Section title="Show only when…" defaultOpen={!!el.visibleIf}>
        <FormulaInput value={el.visibleIf} onChange={(v) => set((x) => (x.visibleIf = v), "vis")} template={template} sample={sample} scope={scope} placeholder="e.g. order.sot_total_discount > 0" />
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ table
function ColumnEditor({ col, table, template, sample, setCol }) {
  const rowScope = { kind: "row", tableId: table.id };
  return (
    <div className="flex flex-col gap-2">
      <Grid>
        <Field label="Header">
          <TextInput value={col.header} onChange={(v) => setCol((c) => (c.header = v), "h")} />
        </Field>
        <Field label="Key (for col.key)">
          <TextInput mono value={col.key} onChange={(v) => setCol((c) => (c.key = v.replace(/[^A-Za-z0-9_]/g, "_")), "k")} />
        </Field>
      </Grid>
      <Field label="Cell value (formula, evaluated per row)">
        <FormulaInput value={col.expr} onChange={(v) => setCol((c) => (c.expr = v), "e")} template={template} sample={sample} scope={rowScope} format={col.format} placeholder="e.g. col.qty * col.rate  ·  PREV(col.balance, 0) + col.amount" />
      </Field>
      <Grid>
        <Field label="Format">
          <FormatSelect value={col.format} onChange={(v) => setCol((c) => (c.format = v))} />
        </Field>
        <Field label="Width (relative)">
          <NumInput value={col.width} min={0.5} onChange={(v) => setCol((c) => (c.width = v), "w")} />
        </Field>
      </Grid>
      <div className="flex flex-wrap gap-2 items-center">
        <Segmented value={col.align || "left"} onChange={(v) => setCol((c) => (c.align = v))} options={ALIGN.slice(0, 3)} />
        <Check checked={!!col.nowrap} onChange={(v) => setCol((c) => (c.nowrap = v))} label="No wrap" />
        <Check checked={!!col.style?.bold} onChange={(v) => setCol((c) => (c.style = { ...(c.style || {}), bold: v }))} label="Bold" />
      </div>
      {table.showFooter && (
        <Field label="Footer total (formula)">
          <FormulaInput value={col.footer} onChange={(v) => setCol((c) => (c.footer = v), "f")} template={template} sample={sample} scope={{ kind: "footer", tableId: table.id }} format={col.footerFormat || col.format} placeholder={`SUM(col.${col.key || "amount"})`} rows={1} />
        </Field>
      )}
      <Field label="Show column only when…">
        <FormulaInput value={col.visibleIf} onChange={(v) => setCol((c) => (c.visibleIf = v), "v")} template={template} sample={sample} scope={{ kind: "doc" }} rows={1} placeholder="e.g. SUM(items.item_discount_amount) > 0" />
      </Field>
    </div>
  );
}

function TableProps({ el, band, template, sample, setEl, selection, setSelection, update }) {
  const set = setEl;
  const ts = el.style || {};
  const hs = el.headerStyle || {};
  const st = (patch, key) => set((x) => (x.style = { ...(x.style || {}), ...patch }), key);
  const hst = (patch, key) => set((x) => (x.headerStyle = { ...(x.headerStyle || {}), ...patch }), key);
  const fs = el.footerStyle || {};
  const fst = (patch, key) => set((x) => (x.footerStyle = { ...(x.footerStyle || {}), ...patch }), key);
  const col = (el.columns || []).find((c) => c.id === selection.colId);
  const setCol = (fn, key) => set((x) => fn(x.columns.find((c) => c.id === selection.colId)), key && "col-" + key);
  const dt = getDocType(template.docType);
  const sources = [
    { value: "items", label: "Items" },
    ...(dt.batch ? [{ value: "batches", label: "Items × batches" }] : []),
    { value: "tax", label: "GST summary (by rate)" },
    ...(dt.payments ? [{ value: "payments", label: "Payments" }] : []),
    ...(dt.creditNotes ? [{ value: "creditNotes", label: "Credit notes" }] : []),
  ];
  const moveCol = (i, dir) =>
    set((x) => {
      const j = i + dir;
      if (j < 0 || j >= x.columns.length) return;
      const [c] = x.columns.splice(i, 1);
      x.columns.splice(j, 0, c);
    });
  return (
    <>
      <div className="px-3 py-2 flex items-center justify-between border-b border-EAECF0">
        <span className="text-xs font-semibold text-000000">Table</span>
        {band.type !== "table" && (
          <IconBtn
            danger
            title="Delete table"
            onClick={() => {
              update((t) => {
                const b = t.bands.find((q) => q.id === band.id);
                b.elements = b.elements.filter((q) => q.id !== el.id);
              });
              setSelection({ bandId: band.id, ids: [], colId: null });
            }}
          >
            <Trash2 size={14} />
          </IconBtn>
        )}
      </div>
      <Section title={`Columns (${(el.columns || []).length})`}>
        <div className="flex flex-col gap-1">
          {(el.columns || []).map((c, i) => (
            <div
              key={c.id}
              className={`flex items-center gap-1 border rounded px-2 py-1 text-[11px] cursor-pointer ${c.id === selection.colId ? "border-primary bg-f3f8ff" : "border-EAECF0"}`}
              onClick={() => setSelection({ ...selection, colId: c.id })}
            >
              <span className="flex-1 truncate">
                {c.header || "(no header)"} <span className="font-mono text-667085">{c.key}</span>
              </span>
              <button type="button" className="p-0.5 disabled:opacity-30" disabled={i === 0} onClick={(e) => {
                  e.stopPropagation();
                  moveCol(i, -1);
                }}>
                <ChevronUp size={13} />
              </button>
              <button type="button" className="p-0.5 disabled:opacity-30" disabled={i === el.columns.length - 1} onClick={(e) => {
                  e.stopPropagation();
                  moveCol(i, 1);
                }}>
                <ChevronDown size={13} />
              </button>
              <button
                type="button"
                className="p-0.5 hover:text-F51D00"
                onClick={(e) => {
                  e.stopPropagation();
                  set((x) => (x.columns = x.columns.filter((q) => q.id !== c.id)));
                }}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="flex items-center justify-center gap-1 border border-dashed border-primary text-primary rounded py-1 text-[11px]"
            onClick={() => {
              const used = new Set((el.columns || []).map((c) => c.key));
              let n = (el.columns || []).length + 1;
              while (used.has(`col${n}`)) n++;
              const c = newColumn({ key: `col${n}`, header: "New column", expr: "" });
              set((x) => x.columns.push(c));
              setSelection({ ...selection, colId: c.id });
            }}
          >
            <Plus size={13} /> Add column
          </button>
          <p className="text-[10px] text-667085">Tip: click a header on the page to select it; drag the blue handles between headers to resize.</p>
        </div>
      </Section>
      {col && (
        <Section title={`Column: ${col.header || col.key}`}>
          <ColumnEditor col={col} table={el} template={template} sample={sample} setCol={setCol} />
        </Section>
      )}
      <Section title="Rows" defaultOpen={!col}>
        <Grid>
          <Field label="Rows come from">
            <Select value={el.source || "items"} onChange={(v) => set((x) => (x.source = v))} options={sources} />
          </Field>
          <Field label="Table name">
            <TextInput mono value={el.name} onChange={(v) => set((x) => (x.name = v.replace(/[^A-Za-z0-9_]/g, "_")), "name")} />
          </Field>
        </Grid>
        <Field label="Only rows where… (optional)">
          <FormulaInput value={el.filter} onChange={(v) => set((x) => (x.filter = v), "filter")} template={template} sample={sample} scope={{ kind: "row", tableId: el.id }} rows={1} placeholder="e.g. item_quantity > 0" showResult={false} />
        </Field>
        <Grid>
          <Field label="Sort by (optional)">
            <TextInput mono value={el.sortBy} onChange={(v) => set((x) => (x.sortBy = v), "sort")} placeholder="item_name" />
          </Field>
          <Field label="Order">
            <Select value={el.sortDir || "asc"} onChange={(v) => set((x) => (x.sortDir = v))} options={[{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }]} />
          </Field>
        </Grid>
        <Grid>
          <Field label="Pad with empty rows to">
            <NumInput value={el.minRows || 0} step={1} min={0} max={100} onChange={(v) => set((x) => (x.minRows = Math.round(v)), "minRows")} />
          </Field>
          <Field label="Row height (mm)">
            <NumInput value={ts.rowHeight || 0} step={0.5} min={0} onChange={(v) => st({ rowHeight: v }, "rh")} placeholder="auto" />
          </Field>
        </Grid>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Check checked={el.showHeader !== false} onChange={(v) => set((x) => (x.showHeader = v))} label="Header row" />
          <Check checked={el.repeatHeader !== false} onChange={(v) => set((x) => (x.repeatHeader = v))} label="Repeat header on each page" />
          <Check checked={!!el.showFooter} onChange={(v) => set((x) => (x.showFooter = v))} label="Totals row" />
        </div>
      </Section>
      <Section title="Table style" defaultOpen={false}>
        <Grid>
          <Field label="Font size (pt)">
            <NumInput value={ts.fontSize ?? 8} step={0.5} min={4} onChange={(v) => st({ fontSize: v }, "fs")} />
          </Field>
          <Field label="Font">
            <Select value={ts.fontFamily || ""} onChange={(v) => st({ fontFamily: v || undefined })} options={[{ value: "", label: "Default" }, ...FONT_FAMILIES]} />
          </Field>
        </Grid>
        <Grid>
          <Field label="Grid lines">
            <Select
              value={ts.grid || "all"}
              onChange={(v) => st({ grid: v })}
              options={[
                { value: "all", label: "All cells" },
                { value: "rows", label: "Between rows" },
                { value: "columns", label: "Between columns" },
                { value: "header", label: "Header only" },
                { value: "outer", label: "Outer border" },
                { value: "none", label: "None" },
              ]}
            />
          </Field>
          <Field label="Line (pt)">
            <NumInput value={ts.borderWidth ?? 0.5} step={0.25} min={0} onChange={(v) => st({ borderWidth: v }, "bw")} />
          </Field>
        </Grid>
        <Field label="Line colour">
          <ColorInput value={ts.borderColor || "#000000"} onChange={(v) => st({ borderColor: v }, "bc")} allowEmpty={false} />
        </Field>
        <Grid>
          <Field label="Cell padding (mm)">
            <NumInput value={ts.cellPadding ?? 1} step={0.25} min={0} onChange={(v) => st({ cellPadding: v }, "cp")} />
          </Field>
          <Field label="Vertical align">
            <Select value={ts.valign || "top"} onChange={(v) => st({ valign: v })} options={VALIGN.map((o) => ({ value: o.id, label: o.label }))} />
          </Field>
        </Grid>
        <Field label="Alternate row colour">
          <ColorInput value={ts.zebra} onChange={(v) => st({ zebra: v }, "zb")} />
        </Field>
        <div className="text-[11px] font-semibold text-344054 pt-1">Header</div>
        <Grid>
          <Field label="Fill">
            <ColorInput value={hs.bg} onChange={(v) => hst({ bg: v }, "hbg")} />
          </Field>
          <Field label="Text colour">
            <ColorInput value={hs.color} onChange={(v) => hst({ color: v }, "hc")} />
          </Field>
        </Grid>
        <Grid>
          <Field label="Size (pt)">
            <NumInput value={hs.fontSize ?? ts.fontSize ?? 8} step={0.5} min={4} onChange={(v) => hst({ fontSize: v }, "hfs")} />
          </Field>
          <Field label=" ">
            <Check checked={hs.bold !== false} onChange={(v) => hst({ bold: v })} label="Bold" />
          </Field>
        </Grid>
        {el.showFooter && (
          <>
            <div className="text-[11px] font-semibold text-344054 pt-1">Totals row</div>
            <Grid>
              <Field label="Fill">
                <ColorInput value={fs.bg} onChange={(v) => fst({ bg: v }, "fbg")} />
              </Field>
              <Field label="Text colour">
                <ColorInput value={fs.color} onChange={(v) => fst({ color: v }, "fc")} />
              </Field>
            </Grid>
            <Grid>
              <Field label="Size (pt)">
                <NumInput value={fs.fontSize ?? ts.fontSize ?? 8} step={0.5} min={4} onChange={(v) => fst({ fontSize: v }, "ffs")} />
              </Field>
              <Field label=" ">
                <Check checked={fs.bold !== false} onChange={(v) => fst({ bold: v })} label="Bold" />
              </Field>
            </Grid>
          </>
        )}
      </Section>
      <Section title="Position (mm)" defaultOpen={false}>
        <Grid>
          <Field label="Left">
            <NumInput value={el.x || 0} min={0} onChange={(v) => set((x) => (x.x = v), "x")} />
          </Field>
          <Field label="Width">
            <NumInput value={el.w} min={10} onChange={(v) => set((x) => (x.w = v), "w")} />
          </Field>
        </Grid>
        {band.type !== "table" && (
          <Grid>
            <Field label="Top">
              <NumInput value={el.y || 0} min={0} onChange={(v) => set((x) => (x.y = v), "y")} />
            </Field>
            <Field label="Height">
              <NumInput value={el.h} min={2} onChange={(v) => set((x) => (x.h = v), "h")} />
            </Field>
          </Grid>
        )}
      </Section>
      <Section title="Show only when…" defaultOpen={!!el.visibleIf}>
        <FormulaInput value={el.visibleIf} onChange={(v) => set((x) => (x.visibleIf = v), "vis")} template={template} sample={sample} scope={{ kind: "doc" }} placeholder='e.g. COUNT("tax") > 0' />
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ band
function BandProps({ band, template, sample, setBand, update, setSelection }) {
  const set = setBand;
  return (
    <>
      <div className="px-3 py-2 flex items-center justify-between border-b border-EAECF0">
        <span className="text-xs font-semibold text-000000">{band.type === "table" ? "Table section" : "Free section"}</span>
        <IconBtn
          danger
          title="Delete section"
          onClick={() => {
            if (!window.confirm(`Delete section "${band.name || "Section"}" and everything in it?`)) return;
            update((t) => (t.bands = t.bands.filter((x) => x.id !== band.id)));
            setSelection({ bandId: null, ids: [], colId: null });
          }}
        >
          <Trash2 size={14} />
        </IconBtn>
      </div>
      <Section title="Section">
        <Field label="Name">
          <TextInput value={band.name} onChange={(v) => set((b) => (b.name = v), "name")} />
        </Field>
        {band.type === "fixed" ? (
          <>
            <Field label="Height (mm)">
              <NumInput value={band.height} min={1} onChange={(v) => set((b) => (b.height = v), "height")} />
            </Field>
            <Field label="Repeat on every page" hint={ZP.paperSize(template).continuous ? "Not used on roll (thermal) paper." : "Top: like a letterhead. Bottom: like a page footer."}>
              <Select
                value={band.repeat || "none"}
                onChange={(v) => set((b) => (b.repeat = v))}
                options={[
                  { value: "none", label: "No — print once" },
                  { value: "top", label: "Yes — at the top of every page" },
                  { value: "bottom", label: "Yes — at the bottom of every page" },
                ]}
              />
            </Field>
            <Field label="Background">
              <ColorInput value={band.bg} onChange={(v) => set((b) => (b.bg = v), "bg")} />
            </Field>
            <Check checked={band.keepTogether !== false} onChange={(v) => set((b) => (b.keepTogether = v))} label="Keep on one page" />
          </>
        ) : (
          <Grid>
            <Field label="Space above (mm)">
              <NumInput value={band.paddingTop || 0} min={0} onChange={(v) => set((b) => (b.paddingTop = v), "pt")} />
            </Field>
            <Field label="Space below (mm)">
              <NumInput value={band.paddingBottom || 0} min={0} onChange={(v) => set((b) => (b.paddingBottom = v), "pb")} />
            </Field>
          </Grid>
        )}
        <Check checked={!!band.pageBreakBefore} onChange={(v) => set((b) => (b.pageBreakBefore = v))} label="Start on a new page" />
      </Section>
      <Section title="Show only when…" defaultOpen={!!band.visibleIf}>
        <FormulaInput value={band.visibleIf} onChange={(v) => set((b) => (b.visibleIf = v), "vis")} template={template} sample={sample} scope={{ kind: "doc" }} placeholder="e.g. order.tcs_or_tds_amount > 0" />
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ document
function scaleLayout(t, factor) {
  const r = (n) => Math.round(n * factor * 100) / 100;
  t.bands.forEach((b) =>
    (b.elements || []).forEach((e) => {
      e.x = r(e.x || 0);
      e.w = r(e.w || 0);
    })
  );
}

function DocumentProps({ template, sample, update }) {
  const p = template.paper || {};
  const ps = ZP.paperSize(template);
  const d = template.defaults || {};
  const [scale, setScale] = React.useState(true);
  const changePaper = (patch) =>
    update((t) => {
      const before = ZP.paperSize(t).contentWidth;
      t.paper = { ...t.paper, ...patch, margins: { ...(t.paper.margins || {}), ...(patch.margins || {}) } };
      const after = ZP.paperSize(t).contentWidth;
      if (scale && before > 0 && Math.abs(after - before) > 0.01) scaleLayout(t, after / before);
    });
  return (
    <>
      <Section title="Format">
        <Field label="Name">
          <TextInput value={template.name} onChange={(v) => update((t) => (t.name = v), { key: "doc-name" })} />
        </Field>
        <Field label="Document">
          <Select value={template.docType} onChange={(v) => update((t) => (t.docType = v))} options={DOC_TYPES.map((x) => ({ value: x.key, label: `${x.group} · ${x.label}` }))} />
        </Field>
      </Section>
      <Section title="Paper">
        <div className="grid grid-cols-3 gap-1">
          {PAPER_PRESETS.map((pp) => (
            <button
              key={pp.id}
              type="button"
              onClick={() =>
                update((t) => {
                  const before = ZP.paperSize(t).contentWidth;
                  t.paper = paperFromPreset(pp.id, t.paper);
                  const after = ZP.paperSize(t).contentWidth;
                  if (scale && before > 0 && Math.abs(after - before) > 0.01) scaleLayout(t, after / before);
                })
              }
              className={`border rounded px-1 py-1.5 text-[10px] leading-tight ${p.preset === pp.id ? "border-primary bg-primary text-white" : "border-EAECF0 text-344054 hover:border-primary"}`}
            >
              <div className="font-semibold">{pp.label}</div>
              <div className={p.preset === pp.id ? "text-white" : "text-667085"}>{pp.desc}</div>
            </button>
          ))}
        </div>
        <Check checked={scale} onChange={setScale} label="Stretch layout to the new paper width" />
        {p.preset === "CUSTOM" && (
          <>
            <Grid>
              <Field label="Width (mm)">
                <NumInput value={p.width} min={20} max={1000} onChange={(v) => changePaper({ width: v })} />
              </Field>
              <Field label="Height (mm)">
                <NumInput value={p.height} min={20} max={2000} disabled={p.continuous} onChange={(v) => changePaper({ height: v })} />
              </Field>
            </Grid>
            <Check checked={!!p.continuous} onChange={(v) => changePaper({ continuous: v })} label="Continuous roll (height follows the content)" />
          </>
        )}
        {!ps.continuous && (
          <Field label="Orientation">
            <Segmented value={p.orientation || "portrait"} onChange={(v) => changePaper({ orientation: v })} options={[{ id: "portrait", label: "Portrait" }, { id: "landscape", label: "Landscape" }]} />
          </Field>
        )}
        <Field label="Margins (mm): top, right, bottom, left">
          <Grid cols={4}>
            {["top", "right", "bottom", "left"].map((k) => (
              <NumInput key={k} value={(p.margins || {})[k] ?? 5} min={0} max={50} onChange={(v) => changePaper({ margins: { [k]: v } })} />
            ))}
          </Grid>
        </Field>
        <div className="text-[10px] text-667085">
          Printable area: {Math.round(ps.contentWidth * 10) / 10} mm wide{ps.continuous ? ", any length" : ` × ${Math.round(ps.contentHeight * 10) / 10} mm`}
        </div>
      </Section>
      <Section title="Default text">
        <Grid>
          <Field label="Font">
            <Select value={d.fontFamily || "Arial"} onChange={(v) => update((t) => (t.defaults = { ...t.defaults, fontFamily: v }))} options={FONT_FAMILIES} />
          </Field>
          <Field label="Size (pt)">
            <NumInput value={d.fontSize ?? 9} step={0.5} min={4} max={30} onChange={(v) => update((t) => (t.defaults = { ...t.defaults, fontSize: v }), { key: "dfs" })} />
          </Field>
        </Grid>
        <Field label="Colour">
          <ColorInput value={d.color || "#000000"} onChange={(v) => update((t) => (t.defaults = { ...t.defaults, color: v }), { key: "dc" })} allowEmpty={false} />
        </Field>
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ multi
function MultiProps({ band, ids, update, setSelection }) {
  const align = (mode) =>
    update((t) => {
      const b = t.bands.find((q) => q.id === band.id);
      const els = b.elements.filter((e) => ids.includes(e.id));
      const minX = Math.min(...els.map((e) => e.x));
      const maxX = Math.max(...els.map((e) => e.x + e.w));
      const minY = Math.min(...els.map((e) => e.y));
      const maxY = Math.max(...els.map((e) => e.y + e.h));
      els.forEach((e) => {
        if (mode === "left") e.x = minX;
        if (mode === "right") e.x = maxX - e.w;
        if (mode === "hcenter") e.x = (minX + maxX) / 2 - e.w / 2;
        if (mode === "top") e.y = minY;
        if (mode === "bottom") e.y = maxY - e.h;
        if (mode === "vcenter") e.y = (minY + maxY) / 2 - e.h / 2;
      });
      if (mode === "hdist" || mode === "vdist") {
        const key = mode === "hdist" ? "x" : "y";
        const size = mode === "hdist" ? "w" : "h";
        const sorted = [...els].sort((a, b2) => a[key] - b2[key]);
        const total = sorted.reduce((a, e) => a + e[size], 0);
        const span = sorted[sorted.length - 1][key] + sorted[sorted.length - 1][size] - sorted[0][key];
        const gap = (span - total) / Math.max(1, sorted.length - 1);
        let pos = sorted[0][key];
        sorted.forEach((e) => {
          e[key] = Math.round(pos * 100) / 100;
          pos += e[size] + gap;
        });
      }
      if (mode === "samew") els.forEach((e) => (e.w = els[0].w));
      if (mode === "sameh") els.forEach((e) => (e.h = els[0].h));
    });
  return (
    <>
      <div className="px-3 py-2 text-xs font-semibold text-000000 border-b border-EAECF0">{ids.length} elements selected</div>
      <Section title="Align">
        <div className="flex flex-wrap gap-1">
          <IconBtn title="Align left" onClick={() => align("left")}>
            <AlignStartVertical size={16} />
          </IconBtn>
          <IconBtn title="Align centers horizontally" onClick={() => align("hcenter")}>
            <AlignCenterVertical size={16} />
          </IconBtn>
          <IconBtn title="Align right" onClick={() => align("right")}>
            <AlignEndVertical size={16} />
          </IconBtn>
          <IconBtn title="Align top" onClick={() => align("top")}>
            <AlignStartHorizontal size={16} />
          </IconBtn>
          <IconBtn title="Align middles" onClick={() => align("vcenter")}>
            <AlignCenterHorizontal size={16} />
          </IconBtn>
          <IconBtn title="Align bottom" onClick={() => align("bottom")}>
            <AlignEndHorizontal size={16} />
          </IconBtn>
          <IconBtn title="Distribute horizontally" onClick={() => align("hdist")} disabled={ids.length < 3}>
            <AlignHorizontalJustifyCenter size={16} />
          </IconBtn>
          <IconBtn title="Distribute vertically" onClick={() => align("vdist")} disabled={ids.length < 3}>
            <AlignVerticalJustifyCenter size={16} />
          </IconBtn>
        </div>
        <div className="flex gap-2">
          <button type="button" className="text-[11px] text-primary" onClick={() => align("samew")}>
            Same width
          </button>
          <button type="button" className="text-[11px] text-primary" onClick={() => align("sameh")}>
            Same height
          </button>
        </div>
      </Section>
      <div className="p-3">
        <button
          type="button"
          className="text-[11px] text-F51D00 flex items-center gap-1"
          onClick={() => {
            update((t) => {
              const b = t.bands.find((q) => q.id === band.id);
              b.elements = b.elements.filter((e) => !ids.includes(e.id));
            });
            setSelection({ bandId: band.id, ids: [], colId: null });
          }}
        >
          <Trash2 size={13} /> Delete selected
        </button>
      </div>
    </>
  );
}

export default function PropertiesPanel({ template, sample, selection, setSelection, update }) {
  const band = template.bands.find((b) => b.id === selection.bandId);
  const els = band ? (band.elements || []).filter((e) => selection.ids.includes(e.id)) : [];
  const el = els.length === 1 ? els[0] : null;
  const setEl = (fn, key) =>
    update(
      (t) => {
        const b = t.bands.find((q) => q.id === band.id);
        fn(b.elements.find((q) => q.id === el.id));
      },
      { key: key ? `${el.id}-${key}` : undefined }
    );
  const setBand = (fn, key) => update((t) => fn(t.bands.find((q) => q.id === band.id)), { key: key ? `${band.id}-${key}` : undefined });
  let body;
  if (el && el.type === "table") body = <TableProps el={el} band={band} template={template} sample={sample} setEl={setEl} selection={selection} setSelection={setSelection} update={update} />;
  else if (el) body = <ElementProps el={el} band={band} template={template} sample={sample} setEl={setEl} update={update} setSelection={setSelection} />;
  else if (els.length > 1) body = <MultiProps band={band} ids={selection.ids} update={update} setSelection={setSelection} />;
  else if (band) body = <BandProps band={band} template={template} sample={sample} setBand={setBand} update={update} setSelection={setSelection} />;
  else body = <DocumentProps template={template} sample={sample} update={update} />;
  return (
    <div className="w-80 shrink-0 border-l border-EAECF0 bg-white overflow-auto min-h-0">
      {body}
    </div>
  );
}
